import { type InputHTMLAttributes, type ReactNode, useEffect, useState } from 'react';

import { Minus } from '#icons/Minus';

import { Check } from '#icons/Check';
import { colors } from '../../../themes';
import {
  CheckboxContainer,
  Description,
  Label,
  LabelDiv,
  LabelWrapper,
  StyledCheckbox,
  StyledInput,
  HoverCheckboxWrapper,
  InputErrorMessage,
} from './style';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  checked?: boolean;
  label?: string;
  description?: string;
  disabled?: boolean;
  semiChecked?: boolean;
  children?: ReactNode;
  /**
   * Useful for triggering hover state when in table or other components
   * where the checkbox is not directly hovered
   **/
  isHover?: boolean;
  id?: string;
  value?: string | number;
  onChange?: (checked: boolean) => void;
  fullWidth?: boolean;
  errorMessage?: string;
  multilineLabel?: boolean;
  stopEventPropagation?: boolean;
};

export const Checkbox = ({
  checked,
  label,
  description,
  disabled,
  semiChecked,
  isHover,
  onChange,
  children,
  id,
  value,
  fullWidth = false,
  errorMessage,
  multilineLabel = true,
  stopEventPropagation = true,
  ...rest
}: CheckboxProps) => {
  const [isChecked, setIsChecked] = useState(checked || false);
  const [isSemiCheck, setIsSemiChecked] = useState(semiChecked);

  useEffect(() => {
    setIsChecked(checked ?? false);
  }, [checked]);

  useEffect(() => {
    setIsSemiChecked(semiChecked);
  }, [semiChecked]);

  const toggleChecked = (e: React.MouseEvent<HTMLElement>) => {
    // Prevent toggling when clicking on a link inside the checkbox label, not stopping propagation to allow opening in new tab, etc.
    if (e.target instanceof HTMLElement && e.target.closest('a')) {
      return;
    }

    if (stopEventPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (onChange) {
      onChange(!isChecked);
    }

    // Only toggle if the checkbox is uncontrolled
    if (checked === undefined) {
      if (!isSemiCheck) {
        setIsChecked(!isChecked);
      }
    }
  };

  return (
    <CheckboxContainer
      className="checkbox-container"
      fullWidth={fullWidth}
      onClick={!disabled ? toggleChecked : undefined}
    >
      <StyledCheckbox
        disabled={disabled}
        isChecked={isChecked || !!isSemiCheck}
        isHover={!!isHover}
      >
        {!isChecked && !isSemiCheck && !disabled && (
          <HoverCheckboxWrapper className="hover-checkbox" isHover={isHover}>
            <Check color={colors.monochrome.grey40} />
          </HoverCheckboxWrapper>
        )}

        {isChecked && !disabled && <Check color={colors.base.black} />}
        {isChecked && disabled && <Check color={colors.monochrome.grey30} />}
        {!isChecked && isSemiCheck && (
          <Minus width="100%" height="100%" color={colors.base.black} />
        )}
      </StyledCheckbox>

      <StyledInput
        data-testid="checkbox"
        id={id}
        type="checkbox"
        checked={isChecked}
        disabled={disabled}
        readOnly
        value={value}
        {...rest}
      />
      {label && (
        <LabelWrapper>
          <Label htmlFor={id} multilineLabel={multilineLabel}>
            {label}
          </Label>
          {description && <Description>{description}</Description>}
          {errorMessage && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
        </LabelWrapper>
      )}

      {children && (
        <LabelWrapper>
          <LabelDiv>{children}</LabelDiv>
          {description && <Description>{description}</Description>}
          {errorMessage && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
        </LabelWrapper>
      )}
    </CheckboxContainer>
  );
};
